/**
 * Bracket-ref preprocessing shared by the checklist rule evaluator
 * (rule-evaluator.tsx) and the income formula engine (utils/income/formula/).
 * Turns `[whatever is inside brackets]` into a jsep-safe identifier, tracked in
 * a `fieldMap` so callers can recover the original bracket contents, and
 * lowers `AND`/`OR`/`NOT` to `&&`/`||`/`!`.
 *
 * Case is preserved here -- only the JS identifier is lowercased, not the raw
 * bracket contents in `fieldMap`. Callers that need case-insensitive matching
 * (rule-evaluator.tsx's `IsValidRule`) lowercase the formula themselves before
 * calling in; the income formula engine relies on this file NOT doing that,
 * since `[doc:Third Party Verification Of Employment And Income.Employer_Name1]`
 * must stay case-exact to match document_types.name / field_types.name.
 */

export type FieldMap = Record<string, string>;

export interface TransformResult {
  transformed: string;
  fieldMap: FieldMap;
}

export const normalizeFieldName = (label: string): string => {
  // Remove brackets if present, then normalize
  const cleaned = label.replace(/^(\[)(.*?)(\])$/, '$2');
  return (
    'field_' +
    cleaned
      .replace(/([^\w])/g, '_')
      .replace(/(_+)/g, '_')
      .replace(/(^_|_$)/g, '')
      .toLowerCase()
  );
};

export const extractFieldsAndTransform = (formula: string): TransformResult => {
  const fieldMap: FieldMap = {};

  // Use a safer regex and limit the length of the field inside brackets to prevent ReDoS
  // Limit field name to 200 chars and avoid nested brackets
  // Group parts of the regex to make operator precedence explicit: (\[([^\]\n]{1,200})\])
  const SAFE_FIELD_REGEX = /(\[([^\]\n]{1,200})\])/g;
  const transformed = formula.replace(
    SAFE_FIELD_REGEX,
    (_, rawField: string): string => {
      const normalized = normalizeFieldName(rawField);
      fieldMap[normalized] = rawField;
      return normalized;
    },
  );

  return { transformed, fieldMap };
};

export const preprocess = (formula: string) => {
  const { transformed, fieldMap } = extractFieldsAndTransform(formula);
  const cleaned = transformed
    .replace(/\bAND\b/gi, '&&')
    .replace(/\bOR\b/gi, '||')
    .replace(/\bNOT\b/gi, '!')
    .trim();
  return { cleaned, fieldMap };
};
